import React, { useState, useEffect } from "react";

import {
  addDoc,
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";

import { toast } from "react-toastify";

import { handleImageChange, handleImageSubmit } from "../../Utility/utils";

import { db } from "../../Config/Firebase/config";

import ConfirmationModal from "../../Utility/confirmModal";

import "./category.css";

const ModifyCategory = () => {
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [available, setAvailable] = useState(true);
  const [categories, setCategories] = useState([]);

  const [editCategory, setEditCategory] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteCategoryId, setDeleteCategoryId] = useState(null);

  const [uploading, setUploading] = useState(false);

  // FETCH CATEGORIES
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoryCollectionRef = collection(db, "category");

        const data = await getDocs(categoryCollectionRef);

        const categoriesData = data.docs.map((categoryDoc) => ({
          ...categoryDoc.data(),
          id: categoryDoc.id,
        }));

        setCategories(categoriesData);
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast.error("Failed to load categories");
      }
    };

    fetchCategories();
  }, []);

  // ADD CATEGORY
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (title.trim() === "") {
      toast.warning("Please enter category title");
      return;
    }

    if (!file) {
      toast.warning("Please select an image");
      return;
    }

    try {
      setUploading(true);

      // Upload image to Cloudinary
      const downloadURL = await handleImageSubmit(
        file,
        setFile,
        () => {},
        "lts/category",
      );

      if (!downloadURL) {
        throw new Error("Image upload failed");
      }

      // Save category to Firestore
      const categoryData = {
        title: title.trim(),
        image: downloadURL,
        available,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const categoryRef = await addDoc(
        collection(db, "category"),
        categoryData,
      );

      // Add immediately to local list
      setCategories((prevCategories) => [
        ...prevCategories,
        {
          id: categoryRef.id,
          title: title.trim(),
          image: downloadURL,
          available,
        },
      ]);

      toast.success("Category uploaded successfully!");

      // Reset form
      setTitle("");
      setFile(null);
      setAvailable(true);
    } catch (error) {
      console.error("Error uploading category:", error);
      toast.error(error.message || "Something went wrong");
    } finally {
      setUploading(false);
    }
  };

  // EDIT CATEGORY
  const handleEdit = async (event) => {
    event.preventDefault();

    if (!editCategory) {
      return;
    }

    if (title.trim() === "") {
      toast.warning("Please enter category title");
      return;
    }

    try {
      setUploading(true);

      const categoryRef = doc(db, "category", editCategory.id);

      const updates = {
        title: title.trim(),
        available,
        updatedAt: serverTimestamp(),
      };

      // Upload new image only when selected
      if (file) {
        const downloadURL = await handleImageSubmit(
          file,
          setFile,
          () => {},
          "lts/category",
        );

        if (!downloadURL) {
          throw new Error("Image upload failed");
        }

        updates.image = downloadURL;
      }

      await updateDoc(categoryRef, updates);

      // Update local category list
      setCategories((prevCategories) =>
        prevCategories.map((category) =>
          category.id === editCategory.id
            ? {
                ...category,
                title: title.trim(),
                available,
                ...(updates.image ? { image: updates.image } : {}),
              }
            : category,
        ),
      );

      toast.success("Category updated successfully!");

      // Reset edit state
      setEditCategory(null);
      setTitle("");
      setFile(null);
      setAvailable(true);
    } catch (error) {
      console.error("Error updating category:", error);
      toast.error(error.message || "Failed to update category");
    } finally {
      setUploading(false);
    }
  };

  // DELETE CATEGORY
  const handleDelete = async () => {
    try {
      const categoryRef = doc(db, "category", deleteCategoryId);

      await deleteDoc(categoryRef);

      setCategories((prevCategories) =>
        prevCategories.filter((category) => category.id !== deleteCategoryId),
      );

      toast.success("Category deleted successfully!");

      setIsModalOpen(false);
      setDeleteCategoryId(null);
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.error("Failed to delete category");
    }
  };

  // IMAGE CHANGE
  const onImageChange = (event) => {
    handleImageChange(event, setFile);
  };

  // CANCEL EDIT
  const handleCancel = () => {
    setEditCategory(null);
    setTitle("");
    setFile(null);
    setAvailable(true);
  };

  return (
    <div className="category-admin-container">
      <div className="category-admin-heading">
        <h3>LTS Category</h3>
      </div>

      <div className="category-admin-form">
        <form onSubmit={editCategory ? handleEdit : handleSubmit}>
          <div className="form-group">
            <label htmlFor="title">Title:</label>

            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="file">
              {editCategory ? "Choose New Image:" : "Choose Image:"}
            </label>

            <input
              type="file"
              id="file"
              accept="image/*"
              onChange={onImageChange}
              required={!editCategory}
            />
          </div>

          <div className="form-group">
            <label htmlFor="available">Available:</label>

            <select
              id="available"
              value={available}
              onChange={(e) => setAvailable(e.target.value === "true")}
              required
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>

          <button className="btn" type="submit" disabled={uploading}>
            {uploading ? "Uploading..." : editCategory ? "Update" : "Submit"}
          </button>

          {editCategory && (
            <button
              type="button"
              className="btn cancel-btn"
              onClick={handleCancel}
              disabled={uploading}
            >
              Cancel
            </button>
          )}
        </form>
      </div>

      <div className="category-list">
        <h3>Category List</h3>

        <ul>
          {categories.map((category) => (
            <li key={category.id}>
              <span>{category.title}</span>

              <button
                className="btn edit-btn"
                onClick={() => {
                  setEditCategory(category);
                  setTitle(category.title);
                  setAvailable(category.available ?? true);
                  setFile(null);
                }}
              >
                Edit
              </button>

              <button
                className="btn delete-btn"
                onClick={() => {
                  setDeleteCategoryId(category.id);
                  setIsModalOpen(true);
                }}
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      </div>

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setDeleteCategoryId(null);
        }}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default ModifyCategory;
